import {
    Filter,
    ExactFilter,
    RangeFilter,
    isExactFilter,
    ExactSign,
    BoundarySign,
} from 'utils/filter/filter';
import NumberExpression from 'utils/filter/expressions/NumberExpression';

export default function buildNumberFilterFunction(
    filter: Filter<number>,
    precision: number,
    exchangeRate: number
) {
    if (isExactFilter(filter)) {
        const exactFilter = filter as ExactFilter<number>;
        return buildNumberExactFilterFunction(
            exactFilter,
            precision,
            exchangeRate
        );
    } else {
        const rangeFilter = filter as RangeFilter<number>;
        return buildNumberRangeFilterFunction(
            rangeFilter,
            precision,
            exchangeRate
        );
    }
}

function buildNumberExactFilterFunction(
    exactFilter: ExactFilter<number>,
    precision: number,
    exchangeRate: number
) {
    const expression = new NumberExpression(
        exactFilter.value,
        precision,
        exchangeRate
    );
    switch (exactFilter.exactSign) {
        case ExactSign.EQ:
            return expression.testEqual.bind(expression);
        case ExactSign.NEQ:
            return expression.testNotEqual.bind(expression);
        default:
            return () => true;
    }
}

function buildNumberRangeFilterFunction(
    rangeFilter: RangeFilter<number>,
    precision: number,
    exchangeRate: number
) {
    const lowerFunction = buildNumberBoundaryFilterFunction(
        rangeFilter,
        'l',
        precision,
        exchangeRate
    );
    const upperFunction = buildNumberBoundaryFilterFunction(
        rangeFilter,
        'u',
        precision,
        exchangeRate
    );

    if (lowerFunction && upperFunction) {
        return (n: number) => lowerFunction(n) && upperFunction(n);
    } else if (lowerFunction && !upperFunction) {
        return (n: number) => lowerFunction(n);
    } else if (!lowerFunction && upperFunction) {
        return (n: number) => upperFunction(n);
    }
    return (_n: number) => true;
}

function buildNumberBoundaryFilterFunction(
    rangeFilter: RangeFilter<number>,
    boundary: 'l' | 'u',
    precision: number,
    exchangeRate: number
) {
    const targetValue =
        rangeFilter[boundary === 'l' ? 'lowerValue' : 'upperValue'];
    if (targetValue === undefined) {
        return;
    }

    const expression = new NumberExpression(
        targetValue,
        precision,
        exchangeRate
    );
    switch (rangeFilter[boundary === 'l' ? 'lowerSign' : 'upperSign']) {
        case BoundarySign.GT:
            return expression.testGreaterThan.bind(expression);
        case BoundarySign.GTE:
            return expression.testGreaterThanOrEqual.bind(expression);
        case BoundarySign.LT:
            return expression.testLessThan.bind(expression);
        case BoundarySign.LTE:
            return expression.testLessThanOrEqual.bind(expression);
        default:
            return () => true;
    }
}
